import { getConfig, klinikenApi } from "@/api";
import {
    baseSubsetActions,
    BaseSubsetActions,
    baseSubsetMutations,
    BaseSubsetMutations,
    BaseSubsetState,
    baseSubsetState,
    convertToSubsetName,
} from "@/store/modules/helpers/baseSubset";
import qs from "qs";

export const baseState: BaseState = {
    ...baseSubsetState,
};

export type BaseActionsProps<T> = {
    state: BaseState & T;
    commit: <K>(
        event: "reset" | string,
        payload?: K,
        options?: {
            root?: boolean;
        }
    ) => void;
    dispatch: (
        event: string,
        payload?: unknown,
        options?: {
            root?: boolean;
        }
    ) => Promise<unknown>;
    rootGetters: {
        [key: string]: unknown;
    };
    getters: {
        [key: string]: unknown;
    };
};

export type BaseState = BaseSubsetState & {};

export type BaseActions = BaseSubsetActions & {};

export type BaseMutations = BaseSubsetMutations & {};

export type FilterParams = Partial<{
    ordering: string;
}> & {
    [key: string]: string | number | boolean;
};

/**
 * Gemensamma actions för modulen i store
 */
export const baseActions: (props: { moduleName: string; onRemoveSubscriber?: (args: any) => void }) => BaseActions = ({
    moduleName,
    onRemoveSubscriber,
}) => ({
    ...baseSubsetActions({ moduleName, onRemoveSubscriber }),
});

/**
 * Gemensamma mutations för modulen i store
 */
export const baseMutations = <T>({ reset }: { reset: (state: BaseState & T) => void }) => ({
    reset,
    ...baseSubsetMutations,
});

/**
 * Gemensam funktion för att hämta objekt från store eller från BE. Anropas av vardera modul i store.
 */
export const fetchItems = async <STATE, VALUE>({
    filterParams,
    apiUrl,
    state,
    collectionName,
    forceRefresh,
    storeInfo,
    limit = 20,
    page = 1,
    config = {},
}: {
    // Tvingar en ny hämtning av data. Returnerar fortfarande det som finns i store och gör hämtning i bakgrunden
    forceRefresh?: boolean;
    collectionName: keyof STATE;
    state: BaseState & STATE;
    apiUrl: string;
    limit: number;
    page: number;
    filterParams: FilterParams;
    config?: {
        [key: string]: unknown;
        tabbnamn?: string;
    };
    storeInfo?: {
        [key: string]: unknown;
    };
}) => {
    const subsetName = convertToSubsetName({ params: filterParams });
    const offset = limit * (page - 1);

    if (
        !forceRefresh &&
        state.subsets[subsetName] &&
        state.subsets[subsetName].ids[offset] &&
        (state.subsets[subsetName].ids[offset + limit - 1] ||
            (typeof state.subsetTotals[subsetName] === "number" && offset + limit > state.subsetTotals[subsetName]))
    ) {
        return {
            subsetName,
            total: limit === 0 ? Object.values(state.subsets[subsetName].ids).length : state.subsetTotals[subsetName],
            nextPage: page + 1,
        };
    }

    try {
        const response = await klinikenApi.get<
            | {
                  results: VALUE[];
                  count: number;
              }
            | VALUE[]
        >(
            `${apiUrl}?${qs.stringify(
                {
                    limit,
                    ordering: filterParams?.ordering,
                    offset: offset,
                    ...filterParams,
                },
                { indices: false }
            )}`,
            getConfig({
                ...config,
            })
        );

        // I de fall där datan är en array och inte är paginerad
        if (Array.isArray(response.data)) {
            return {
                subsetName,
                offset,
                results: response.data,
                total: response.data.length,
                nextPage: null,
            };
        }

        return {
            subsetName,
            offset,
            results: response.data.results.map((result) => {
                if (storeInfo) {
                    return {
                        ...result,
                        storeInfo,
                    };
                }

                return result;
            }),
            total: response.data.count,
            nextPage: (state[collectionName] as Map<number, unknown>).size < response.data.count ? page + 1 : null,
        };
    } catch (error) {
        console.error(error);

        throw error;
    }
};
